/**
 * Michelangelo Phase 2: Capability Profile & Compliance Surface View
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCapabilityProfile } from '../../hooks/useCapabilityProfile';
import IPCAFAssessment from './IPCAFAssessment';
import './CapabilityProfileView.css';

const CapabilityProfileView = ({ organizationId, userId, userName }) => {
  const { t } = useTranslation(['michelangelo', 'dashboards']);
  const { profile, loading, error, refresh } = useCapabilityProfile(organizationId);
  const [showAssessment, setShowAssessment] = useState(false);

  if (!organizationId) {
    return (
      <div className="capability-profile-view">
        <p className="empty-state">{t('michelangelo:selectOrganization') || 'Select an organization to view capability profile.'}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="capability-profile-view loading">
        <p>{t('michelangelo:loading') || 'Loading…'}</p>
      </div>
    );
  }

  if (showAssessment) {
    return (
      <div className="capability-profile-view">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowAssessment(false)}
        >
          ← {t('michelangelo:backToProfile') || 'Back to profile'}
        </button>
        <IPCAFAssessment
          organizationId={organizationId}
          userId={userId}
          userName={userName}
          onComplete={() => {
            setShowAssessment(false);
            refresh();
          }}
        />
      </div>
    );
  }

  const ipcafRuns = profile?.instruments?.filter((i) => i.instrumentId === 'IPCAF_2019') || [];
  const ipcafEntry = ipcafRuns.length > 0 ? ipcafRuns[ipcafRuns.length - 1] : null;
  const surface = profile?.complianceSurface;

  return (
    <div className="capability-profile-view">
      <div className="profile-header">
        <h2>{t('michelangelo:capabilityProfile') || 'Hospital Capability Profile'}</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!profile && !loading && (
        <div className="empty-state">
          <p>{t('michelangelo:noProfileYet') || 'No capability profile yet. Complete the IPCAF assessment to get started.'}</p>
          <button type="button" className="btn btn-primary" onClick={() => setShowAssessment(true)}>
            {t('michelangelo:startIPCAF') || 'Start IPCAF Assessment'}
          </button>
        </div>
      )}

      {profile && ipcafEntry && (
        <>
          <div className="profile-summary">
            <div className="profile-summary-header">
              <h3>{t('michelangelo:ipcafResults') || 'IPCAF Results'}</h3>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setShowAssessment(true)}
              >
                {t('michelangelo:reassessIPCAF') || 'Re-assess IPCAF'}
              </button>
            </div>
            <div className="summary-cards">
              <div className="summary-card">
                <span className="card-label">{t('michelangelo:maturityLevel') || 'Maturity Level'}</span>
                <span className="card-value">{ipcafEntry.maturityLevel}</span>
              </div>
              <div className="summary-card">
                <span className="card-label">{t('michelangelo:totalScore') || 'Total Score'}</span>
                <span className="card-value">{ipcafEntry.totalScore} / {ipcafEntry.maxPossibleScore}</span>
              </div>
              <div className="summary-card">
                <span className="card-label">{t('michelangelo:scorePercentage') || 'Score %'}</span>
                <span className="card-value">{Math.round(ipcafEntry.scorePercentage || 0)}%</span>
              </div>
            </div>
          </div>

          {surface && (
            <div className="compliance-surface">
              <h3>{t('michelangelo:complianceSurface') || 'Compliance Surface'}</h3>
              <p className="surface-desc">
                {t('michelangelo:complianceSurfaceDesc') || 'Standards that apply to your hospital based on your capability profile.'}
              </p>
              <div className="surface-stats">
                <span>{surface.applicableStandards?.length || 0} {t('michelangelo:applicable') || 'applicable'}</span>
                <span>{surface.excludedStandards?.length || 0} {t('michelangelo:excluded') || 'excluded'}</span>
              </div>
              <div className="standards-list">
                <h4>{t('michelangelo:applicableStandards') || 'Applicable Standards'}</h4>
                <ul>
                  {(surface.applicableStandards || []).slice(0, 30).map((s) => (
                    <li key={s.standardId}>
                      <strong>{s.standardCode}</strong> — {s.standardName}
                      {s.applicabilityReasons?.length > 0 && (
                        <span className="reasons"> ({s.applicabilityReasons.slice(0, 2).join(', ')})</span>
                      )}
                    </li>
                  ))}
                </ul>
                {(surface.applicableStandards?.length || 0) > 30 && (
                  <p className="truncated">… and {surface.applicableStandards.length - 30} more</p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CapabilityProfileView;
